import React from 'react';
import { Alert, AlertTitle, Box, Button, Typography } from '@mui/material';

// Alerta do laboratorio por area + SE (Sessao 29, 23/09/2026 - Walber).
// Mostra o que o motor respondeu em GET /api/fad01/conferencia-laboratorio.
// NAO muda nenhum numero do relatorio. Fica FORA da impressao/PDF: quem usa
// passa a classe que a propria tela ja usa para esconder coisas da impressao.
// Cores: so as do tema MUI (error / warning), para funcionar no claro e no escuro.

export interface ItemRegra { regra: string; mensagem: string; }
export interface BoletimConferencia {
  id: number; data: string; agente: string; travas: ItemRegra[]; avisos: ItemRegra[];
  // Bloco F2 (opcionais: o motor antigo nao mandava)
  quarteirao_texto?: string | null; sem_resultado?: number; tubitos_sem_resultado?: number[];
  congeladas?: number; tubitos_congelados?: number[];
}
export interface GrupoConferencia {
  area_id: number; area_nome: string; se: number;
  soma_imoveis_aeg: number; soma_depositos_aeg: number;
  soma_imoveis_alb: number; soma_depositos_alb: number;
  recusa_sispncd: boolean; boletins: BoletimConferencia[];
}

interface Props {
  grupos: GrupoConferencia[] | null;
  modo: 'semana' | 'resumo';   // semana = FAD-07 Semanal; resumo = Quadro de Positividade
  className?: string;          // classe de "nao imprimir" da tela que usa
  onAbrirFAD01?: (id: number) => void;   // Bloco F2: botao "Abrir boletim no FAD-01" (modo semana)
}

const ddmm = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;

const motivoRecusa = (g: GrupoConferencia) => {
  const partes: string[] = [];
  if (g.soma_imoveis_aeg > g.soma_depositos_aeg)
    partes.push(`Ae. aegypti: ${g.soma_imoveis_aeg} imóveis × ${g.soma_depositos_aeg} depósitos`);
  if (g.soma_imoveis_alb > g.soma_depositos_alb)
    partes.push(`Ae. albopictus: ${g.soma_imoveis_alb} imóveis × ${g.soma_depositos_alb} depósitos`);
  return partes.join('; ');
};

const plural = (n: number, um: string, varios: string) => (n === 1 ? um : varios);

export default function AlertaLaboratorioSemana({ grupos, modo, className, onAbrirFAD01 }: Props) {
  if (!grupos || grupos.length === 0) return null;

  const prefixo = (g: GrupoConferencia) => (modo === 'resumo' ? `${g.area_nome} — SE ${g.se}: ` : '');
  const recusas = grupos.filter(g => g.recusa_sispncd);
  const linhasTrava: string[] = [];
  const linhasAviso: string[] = [];
  grupos.forEach(g => g.boletins.forEach(b => {
    b.travas.forEach(t => linhasTrava.push(`${prefixo(g)}${ddmm(b.data)} — ${b.agente} — ${t.mensagem} (${t.regra})`));
    b.avisos.forEach(a => linhasAviso.push(`${prefixo(g)}${ddmm(b.data)} — ${b.agente} — ${a.mensagem} (${a.regra})`));
  }));

  // Bloco F2: amostras sem resultado e congeladas (so o modo semana mostra).
  const todos = grupos.flatMap(g => g.boletins);
  const comSemResultado = todos.filter(b => (b.sem_resultado ?? 0) > 0);
  const comCongelada = todos.filter(b => (b.congeladas ?? 0) > 0);
  const totalSemResultado = comSemResultado.reduce((s, b) => s + (b.sem_resultado ?? 0), 0);
  const totalCongeladas = comCongelada.reduce((s, b) => s + (b.congeladas ?? 0), 0);

  // Bloco F1/F2: no FAD-07 (modo semana), sem nada a mostrar nao abre caixa vazia.
  if (modo === 'semana' && recusas.length === 0 && linhasTrava.length === 0 && linhasAviso.length === 0
      && comSemResultado.length === 0 && comCongelada.length === 0) return null;

  const botaoAbrir = (id: number) => (onAbrirFAD01 ? (
    <Button variant="outlined" size="small" color="inherit" onClick={() => onAbrirFAD01(id)}
      sx={{ ml: 1, py: 0, minHeight: 28, textTransform: 'none', whiteSpace: 'nowrap' }}>
      Abrir boletim no FAD-01
    </Button>
  ) : null);
  const linhaTubitos = (b: BoletimConferencia, tubitos: number[]) => {
    const qt = (b.quarteirao_texto || '').trim();
    return `${ddmm(b.data)} · ${b.agente} · ${plural(tubitos.length, 'tubito', 'tubitos')} ${tubitos.join(', ')}`
      + (qt ? ` · quarteirão(ões) ${qt}` : '');
  };
  // Linhas de trava/aviso agrupadas por boletim: o botao aparece uma vez por boletim.
  const porBoletim = (tipo: 'travas' | 'avisos') => grupos.flatMap(g => g.boletins
    .filter(b => b[tipo].length > 0)
    .map(b => ({ b, linhas: b[tipo].map(i => `${prefixo(g)}${ddmm(b.data)} — ${b.agente} — ${i.mensagem} (${i.regra})`) })));

  return (
    <Box className={className} sx={{ mb: 2, maxWidth: 900, mx: modo === 'semana' ? 'auto' : undefined }}>
      {recusas.map(g => (
        <Alert key={`r${g.area_id}-${g.se}`} severity="error" variant="filled" sx={{ mb: 1 }}>
          {prefixo(g)}Este FAD-07 será recusado no SISPNCD: imóveis positivos maiores que depósitos
          positivos ({motivoRecusa(g)})
        </Alert>
      ))}

      {modo === 'semana' ? (
        <>
          {comSemResultado.length > 0 && (
            <Alert severity="error" variant="filled" sx={{ mb: 1 }}>
              <AlertTitle sx={{ fontWeight: 700 }}>Não mande este FAD-07 para digitação ainda</AlertTitle>
              {totalSemResultado} {plural(totalSemResultado, 'amostra desta semana ainda sem resultado', 'amostras desta semana ainda sem resultado')}.
              {' '}O resumo do laboratório está incompleto: os positivos dessas amostras não estão sendo contados.
              <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                {comSemResultado.map(b => (
                  <li key={b.id} style={{ marginBottom: 4 }}>
                    {linhaTubitos(b, b.tubitos_sem_resultado ?? [])}{botaoAbrir(b.id)}
                  </li>
                ))}
              </Box>
            </Alert>
          )}
          {linhasTrava.length > 0 && (
            <Alert severity="error" sx={{ mb: 1 }}>
              <AlertTitle sx={{ fontWeight: 700 }}>Boletins com resultado de laboratório inconsistente</AlertTitle>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {porBoletim('travas').map(({ b, linhas }) => linhas.map((l, i) => (
                  <li key={`${b.id}-${i}`}>{l}{i === linhas.length - 1 && botaoAbrir(b.id)}</li>
                )))}
              </Box>
            </Alert>
          )}
          {linhasAviso.length > 0 && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              <AlertTitle sx={{ fontWeight: 700 }}>Atenção — confira</AlertTitle>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {porBoletim('avisos').map(({ b, linhas }) => linhas.map((l, i) => (
                  <li key={`${b.id}-${i}`}>{l}{i === linhas.length - 1 && botaoAbrir(b.id)}</li>
                )))}
              </Box>
            </Alert>
          )}
          {comCongelada.length > 0 && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              <AlertTitle sx={{ fontWeight: 700 }}>Amostra congelada nesta semana</AlertTitle>
              {totalCongeladas} {plural(totalCongeladas, 'amostra congelada', 'amostras congeladas')}: o resultado
              {' '}{plural(totalCongeladas, 'dela', 'delas')} NÃO entra neste FAD-07. Confira se é isso mesmo antes de mandar para digitação.
              <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                {comCongelada.map(b => (
                  <li key={b.id} style={{ marginBottom: 4 }}>
                    {linhaTubitos(b, b.tubitos_congelados ?? [])}{botaoAbrir(b.id)}
                  </li>
                ))}
              </Box>
            </Alert>
          )}
        </>
      ) : (
        grupos.filter(g => g.boletins.length > 0).map(g => {
          const comTrava = g.boletins.filter(b => b.travas.length > 0).length;
          const comAviso = g.boletins.filter(b => b.avisos.length > 0).length;
          return (
            <Alert key={`g${g.area_id}-${g.se}`} severity={comTrava > 0 ? 'error' : 'warning'} sx={{ mb: 1, py: 0 }}>
              {g.area_nome} — SE {g.se}: {comTrava} boletim(ns) com trava, {comAviso} com aviso
            </Alert>
          );
        })
      )}

      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        Para corrigir, abra o boletim no FAD-01.
      </Typography>
    </Box>
  );
}
